import { useState } from 'react'
import type { Project } from '../data/projectTypes'
import { connectedIssueTracker, isAgentConnected, projectAgentPrompt } from '../lib/agentConnection'
import IssueTrackerChatCard from './chat/IssueTrackerChatCard'
import { useGetProjectQuery } from '../services/projectDataApi'
import { CheckCircle2, CircleDashed } from 'lucide-react'
import './TestingSetup.css'
import './chat/chat.css'
import { AgentPrompt } from './AgentPrompt'
import ApiTokenControls from './ApiTokenControls'

export default function AgentConnectionSetup({ project, canEdit }: { project: Project; canEdit: boolean }) {
  const [trackerOpen, setTrackerOpen] = useState(false)
  const query = useGetProjectQuery(project.id)
  const connected = isAgentConnected(query.currentData ?? project)
  const tracker = connectedIssueTracker(project)
  const projectUrl = `${window.location.origin}/projects/${project.id}/overview`
  return <div className="agent-connection testing-settings">
    <p>Connect your coding agent so it can review and fix the bugs QA reports for this project. Or connect an issue tracker and QA will send new bug reports there for your team to resolve.</p>
    <section aria-labelledby="agent-setup-title">
      <h2 id="agent-setup-title">Connect your coding agent</h2>
      <p>Open your app’s codebase in your coding agent and paste the prompt below. It includes your project link and the instructions for accessing QA.</p>
      <p>Project: <a href={projectUrl}>{project.name}</a></p>
      <AgentPrompt analyticsEvent="support-agent-prompt-copy" prompt={projectAgentPrompt(projectUrl)} />
      <h3>Give your agent an API token</h3>
      <p>Create a token below, copy it when it is shown, and provide it to your agent when asked. Tokens belong to your account and work across projects you can access. Ask your agent to read this project’s bug list to complete the connection, even if no bugs have been found yet.</p>
      <ApiTokenControls />
      <div className="agent-connection__status">
        <div className="agent-connection__status-heading">
          <span role="status">{connected ? <CheckCircle2 size={18} aria-hidden /> : <CircleDashed size={18} aria-hidden />}{connected ? 'Agent connected' : 'Agent not connected'}</span>
          <button type="button" disabled={query.isFetching} onClick={() => void query.refetch()}>{query.isFetching ? 'Checking…' : 'Check now'}</button>
        </div>
        {!connected && <p>Your agent must read this project’s bugs list to be marked as connected, even if the list is empty.</p>}
        {query.isError && <p role="alert">Could not check the agent connection. Please try again.</p>}
      </div>
    </section>
    <section aria-labelledby="issue-tracker-title">
      <h2 id="issue-tracker-title">Issue trackers</h2>
      <p>{tracker ? `Connected to ${tracker.name}: ${tracker.destination}. QA sends new bug reports here.` : 'No issue tracker connected. Connect GitHub Issues, GitLab, Linear, Jira, or Asana.'}</p>
      <div className="testing-setup__actions">
        <button type="button" disabled={!canEdit} onClick={() => setTrackerOpen(true)}>
          {tracker ? 'Manage issue tracker' : 'Connect issue tracker'}
        </button>
      </div>
      {canEdit && trackerOpen && <div className="agent-connection__tracker">
        <IssueTrackerChatCard project={project} onClose={() => setTrackerOpen(false)} />
      </div>}
      {!canEdit && <p>A project member can connect an issue tracker.</p>}
    </section>
  </div>
}
